import { canManageClientDocument } from "../../utils/clientContactPermissions";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/reusableComponents/Button";
import { Input } from "../../components/reusableComponents/Input";
import { Label } from "../../components/reusableComponents/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/reusableComponents/Select";
import { Textarea } from "../../components/reusableComponents/Textarea";
import qualifications from "../../utils/qualifications";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";
import { DeleteIcon, Loader2, ShoppingBagIcon } from "lucide-react";
import MultiSelect from "../../components/reusableComponents/MultiSelect";
import { Checkbox, Modal, message } from "antd";
import { cacesList } from "../../utils/caces";
import { habilitationsList } from "../../utils/habilitation";
import LazyLoadingSelectQualification from "../../components/reusableComponents/LazyLoadingSelectQualification";
import {
  buildAnnouncementDraft,
  orderContactDetails,
} from "../../utils/announcementDraft";
import {
  buildOrderPayload,
  clientAddressOptions,
  clientContactOptions,
  createOrderFormInitialState,
  hydrateOrderResponse,
  persistOrder,
} from "./orderForm";

const CreateCommandeModal = ({
  close,
  orders,
  setOrders,
  onCreated,
  onSaved,
  initialOrder = null,
  mode = "create",
}) => {
  const initial = createOrderFormInitialState(initialOrder);
  const isEdit = mode === "edit";
  const isDuplicate = mode === "duplicate";
  const [clientSearch, setClientSearch] = useState(initial.clientSearch);
  const [client, setClient] = useState(initial.clientRecord);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [step, setStep] = useState(0);
  const [extraPeriods, setExtraPeriods] = useState(initial.extraPeriods);
  const steps = ["Client et contact", "Période et horaires", "Poste, lieu et tarif", "Prérequis et validation"];
  const clientRef = useRef();
  const [clientAdressesArray, setClientAdressesArray] = useState(() =>
    clientAddressOptions(initial.clientRecord),
  );
  const [clientContactsArray, setClientContactsArray] = useState(() =>
    clientContactOptions(
      initial.clientRecord,
      (contact) => canManageClientDocument(contact, "orders"),
      initial.formData.contact,
    ),
  );
  const [usedMotif, setUsedMotif] = useState([]);
  const [prime, setPrime] = useState("");
  const [primeType, setPrimeType] = useState("");
  const [taux, setTaux] = useState("");
  const [diplomes, setDiplomes] = useState(initial.diplomes);
  const [habilitations, setHabilitations] = useState(initial.habilitations);
  const [caces, setCaces] = useState(initial.caces);
  const [permis, setPermis] = useState(initial.permis);
  const [missingFields, setMissingFields] = useState([]);

  const [formData, setFormData] = useState(initial.formData);

  const setTask = (value) => {
    setFormData((prev) => ({ ...prev, task: value }));
  };

  const addPrime = () => {
    if (!prime || !primeType || !taux) return;
    const newPrime = { prime, primeType, taux };
    setFormData((prev) => ({
      ...prev,
      primes: [...prev.primes, newPrime],
    }));
    setPrime("");
    setPrimeType("");
    setTaux("");
  };

  const deletePrime = (index) => {
    setFormData((prev) => ({
      ...prev,
      primes: prev.primes.filter((_, i) => i !== index),
    }));
  };

  const selectNombreHeureHebdo = (value) => {
    setFormData((prev) => ({ ...prev, nbHeureHebdo: value }));
  };

  const loadRecours = () => {
    axios
      .get("pld-contract/recours")
      .then((res) => {
        console.log(res.data);
        setUsedMotif(
          res.data
            .map((moti) => moti.IdReferentiel + " : " + moti.Designation)
            .map((motif, idx) => {
              return { value: motif, label: motif, key: idx };
            }),
        );
      })
      .catch((e) => console.log(e));
  };

  const searchClient = (word) => { setClientSearch(word); setShowClientDropdown(true); };
  useEffect(() => {
    if (clientSearch.trim().length < 3 || clientSearch === client?.companyName) { setClients([]); setIsLoadingClient(false); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoadingClient(true);
      try {
        const { data } = await axios.post("client/freelance/find-by-text", { query: clientSearch }, { signal: controller.signal });
        if (!controller.signal.aborted) setClients(data);
      } catch (error) {
        const detail = apiErrorMessage(error, "Impossible de rechercher les clients.");
        if (detail) message.error(detail);
      }
      finally { if (!controller.signal.aborted) setIsLoadingClient(false); }
    }, 300);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [clientSearch, client]);

  useEffect(() => {
    loadRecours();

    const handleClickOutside = (event) => {
      if (clientRef.current && !clientRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const clientId = formData.client?._id;
    if (!initialOrder || client || !clientId) return undefined;
    const controller = new AbortController();
    axios
      .get(`client/${clientId}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setClient(data);
        setClientSearch(data.companyName || formData.client?.name || "");
        setClientAdressesArray(clientAddressOptions(data));
        setClientContactsArray(
          clientContactOptions(
            data,
            (contact) => canManageClientDocument(contact, "orders"),
            formData.contact,
          ),
        );
      })
      .catch((error) => {
        const detail = apiErrorMessage(
          error,
          "Impossible de charger le client de cette commande.",
        );
        if (detail) message.error(detail);
      });
    return () => controller.abort();
  }, [client, formData.client?._id, formData.client?.name, formData.contact, initialOrder]);

  const selectConsigne = (value) => {
    setFormData((prev) => ({ ...prev, consigne: value }));
  };

  
  const selectQuantity = (value) => {
    setFormData((prev) => ({ ...prev, quantity: Math.max(1, value) }));
  };

  const selectTypeContrat = (value) => {
    setExtraPeriods([]);
    setFormData((prev) => ({ ...prev, typeContrat: value, motif: "", justificatif: "", endDate: value === "CDI" ? "" : prev.endDate }));
  };

  const selectContact = (value) => {
    setFormData((prev) => ({ ...prev, contact: value }));
  };

  const selectMotif = (value) => {
    setFormData((prev) => ({ ...prev, motif: value }));
  };

  const licencesArray = [
    "AM",
    "A1",
    "A2",
    "A",
    "B1",
    "B",
    "C1",
    "C",
    "D1",
    "D",
    "BE",
    "C1E",
    "CE",
    "D1E",
    "DE",
  ];

  const diplomasArray = [
    "Aide soignant",
    "infirmier",
    "AES",
    "AMP",
    "BTS analyse biologique",
    "BP préparateur en pharmacie",
    "CAP - BEP",
    "Baccalauréat",
    "DEUG - BTS - DUT - DEUST",
    "Licence - BUT",
    "Maîtrise - Master",
    "Doctorat",
  ];

  const setAdressFromSiege = () => {
    if (!client) return;
    setFormData((prev) => ({
      ...prev,
      address: client.address || "",
      postCode: client.postCode || "",
      town: client.town || "",
    }));
  };

  const setAdressFromLivraison = () => {
    if (!client) return;
    setFormData((prev) => ({
      ...prev,
      address: client.addressLivraison || "",
      postCode: client.postCodeLivraison || "",
      town: client.townLivraison || "",
    }));
  };

  const setAdressFromFacturation = () => {
    if (!client) return;
    setFormData((prev) => ({
      ...prev,
      address: client.addressFacturation || "",
      postCode: client.postCodeFacturation || "",
      town: client.townFacturation || "",
    }));
  };

  const multipleDates = ["Vacation", "CDD"].includes(formData.typeContrat);
  const periods = [{startDate:formData.startDate,endDate:formData.startDate,startHour:formData.startHour,endHour:formData.endHour}, ...extraPeriods];
  const fieldsByStep = [
    [{ key: "client", label: "Client" }, { key: "contact", label: "Contact client" }],
    [{ key: "typeContrat", label: "Type de contrat" }, { key: "startDate", label: "Date de début" }, ...(!multipleDates && formData.typeContrat !== "CDI" ? [{ key: "endDate", label: "Date de fin" }] : []), ...(formData.typeContrat === "Intérim" ? [{ key: "motif", label: "Motif" }, { key: "justificatif", label: "Justificatif" }] : []), { key: "startHour", label: "Heure de début" }, { key: "endHour", label: "Heure de fin" }, { key: "nbHeureHebdo", label: "Heures hebdomadaires" }],
    [{ key: "qualification", label: "Qualification" }, { key: "clientName", label: "Client final" }, { key: "address", label: "Adresse" }, { key: "postCode", label: "Code postal" }, { key: "town", label: "Ville" }, { key: "hourlyRate", label: "Taux horaire" }],
    [{ key: "task", label: "Missions" }],
  ];
  const validateStep = (index) => {
    const missing = fieldsByStep[index].filter(({ key }) => Array.isArray(formData[key]) ? !formData[key].length : !formData[key]);
    setMissingFields(missing.map(field => field.key));
    if (index === 1 && multipleDates && periods.some(period => !period.startDate || !period.startHour || !period.endHour)) { message.error("Renseignez la date et les horaires de chaque journée."); return false; }
    if (missing.length) { message.error(`À compléter : ${missing.map(field => field.label).join(", ")}`); return false; }
    if (index === 1 && formData.endDate && formData.endDate < formData.startDate) { message.error("La date de fin doit être postérieure au début."); return false; }
    if (index === 1 && Number(formData.nbHeureHebdo) <= 0) { message.error("Les heures hebdomadaires doivent être positives."); return false; }
    if (index === 2 && (Number(formData.hourlyRate) <= 0 || !Number.isInteger(Number(formData.quantity)) || Number(formData.quantity) < 1)) { message.error("Renseignez un taux positif et un nombre de personnes entier supérieur à zéro."); return false; }
    return true;
  };
  const saveCommande = (e) => {
    e.preventDefault();
    if (isLoading) return;
    if (step < 3) { if (validateStep(step)) setStep(step + 1); return; }
    for (let index = 0; index < 4; index++) if (!validateStep(index)) { setStep(index); return; }
    const payload = buildOrderPayload({
      formData,
      diplomes,
      habilitations,
      caces,
      permis,
      extraPeriods,
    });
    setIsLoading(true);
    persistOrder(axios, mode, initialOrder?._id, payload)
      .then((res) => {
        const newCommande = hydrateOrderResponse(
          res.data,
          isEdit ? initialOrder : {},
        );
        const draftSource = {
          ...payload,
          clientRecord: client,
        };
        const contactData = orderContactDetails(newCommande, draftSource);
        const savedCommande = {
          ...newCommande,
          contactData,
        };
        setOrders((current) => {
          const list = Array.isArray(current)
            ? current
            : Array.isArray(orders)
              ? orders
              : [];
          if (isEdit) {
            return list.map((order) =>
              order._id === savedCommande._id
                ? { ...order, ...savedCommande, loading: false }
                : order,
            );
          }
          return [
            { ...savedCommande, loading: false },
            ...list.filter((order) => order._id !== savedCommande._id),
          ];
        });
        onSaved?.(savedCommande);
        if (!isEdit) {
          onCreated?.({
            commande: savedCommande,
            announcementDraft: buildAnnouncementDraft(
              newCommande,
              draftSource,
            ),
          });
        }
        close();
      })
      .catch((error) =>
        message.error(
          apiErrorMessage(
            error,
            isEdit
              ? "La commande n’a pas pu être modifiée. Vos informations sont conservées, réessayez."
              : "La commande n’a pas pu être créée. Vos informations sont conservées, réessayez.",
          ),
        ),
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal open centered width={950} footer={null} onCancel={() => { if (!isLoading) close(); }} keyboard={!isLoading} closable={!isLoading} maskClosable={!isLoading} title={<div className="flex items-center gap-3"><div className="rounded-xl bg-blue-50 p-3 text-blue-700"><ShoppingBagIcon size={22} /></div><div><h2 className="text-xl font-semibold text-slate-900">{isEdit ? "Modifier la commande" : isDuplicate ? "Dupliquer la commande" : "Nouvelle commande"}</h2><p className="mt-1 text-sm font-normal text-slate-500">{isEdit ? "Mettez à jour le besoin sans modifier son suivi candidat." : isDuplicate ? "Vérifiez la copie avant de créer une nouvelle commande." : "Définissez le besoin du client et les conditions de la mission."}</p></div></div>}>
      <ol className="my-5 grid grid-cols-2 gap-2 sm:grid-cols-4">{steps.map((label, index) => <li key={label} aria-current={step === index ? "step" : undefined} className={`rounded-lg p-3 text-xs ${step === index ? "bg-blue-50 font-semibold text-blue-800" : "bg-slate-50 text-slate-500"}`}>{index + 1}. {label}</li>)}</ol>
      {formData.client && <p className="mb-4 rounded-lg bg-slate-50 px-4 py-2 text-sm text-slate-600"><strong>{formData.client.name}</strong>{formData.typeContrat && ` · ${formData.typeContrat}`}{formData.qualification && ` · ${formData.qualification}`} · {formData.quantity} personne(s)</p>}
      <form onSubmit={saveCommande}>
        <div className="max-h-[55vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-5 [&_input]:bg-white [&_textarea]:bg-white [&_button[role=combobox]]:bg-white">
        <h3 className="mb-5 text-lg font-semibold text-slate-900">{steps[step]}</h3>
        <section hidden={step !== 0}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
            <div ref={clientRef} className="space-y-2">
              <Label htmlFor="client">Client</Label>
              <div className="relative">
                <Input
                  id="client"
                  value={clientSearch}
                  onFocus={() => {
                    setShowClientDropdown(true);
                  }}
                  onChange={(e) => searchClient(e.target.value)}
                  placeholder="Rechercher le client"
                  className="w-full border border-gray-300 rounded-md pr-10 "
                />
                {isLoadingClient && (
                  <div className="absolute inset-y-0 right-3 flex items-center">
                    <svg
                      className="animate-spin h-4 w-4 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                  </div>
                )}
              </div>

              {showClientDropdown && clients.length > 0 && (
                <ul className="z-10 mt-1 w-11/12 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  {clients.map((client) => (
                    <li
                      key={client._id}
                      onClick={() => {
                        setFormData({
                          ...formData,
                          client: { _id: client._id, name: client.companyName },
                          clientName: client.companyName || "", address: client.address || "", postCode: client.postCode || "", town: client.town || "", contact: "",
                        });
                        setClient(client);
                        setClientSearch(client.companyName);
                        setShowClientDropdown(false);
                        setClientAdressesArray(clientAddressOptions(client));
                        setClientContactsArray(
                          clientContactOptions(
                            client,
                            (contact) =>
                              canManageClientDocument(contact, "orders"),
                          ),
                        );
                      }}
                      className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                        formData.client?._id === client._id
                          ? "bg-blue-100 font-semibold"
                          : ""
                      }`}
                    >
                      {client.companyName}
                    </li>
                  ))}
                </ul>
              )}

              {formData.client && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <span
                    key={formData.client._id}
                    className="flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full"
                  >
                    {formData.client.name}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, client: null, contact: "", clientName: "", address: "", postCode: "", town: "" });
                        setClient(null); setClientSearch(""); setClientContactsArray([]); setClientAdressesArray([]);
                      }}
                      className="ml-2 text-blue-500 hover:text-blue-700 focus:outline-none"
                    >
                      &times;
                    </button>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {formData.client && (
              <>
                <div className="w-1/2 flex flex-col space-y-2">
                  <Label className="" htmlFor="contact">
                    Contact{" "}
                  </Label>
                  <Select value={formData.contact || undefined} onValueChange={selectContact} name="contact">
                    <SelectTrigger
                      className={`w-full ${missingFields.includes("contact") ? "border-red-500" : "border-gray-300"}`}
                      id="contact"
                    >
                      <SelectValue placeholder="Sélectionnez un contact" />
                    </SelectTrigger>
                    <SelectContent className="z-[1100] bg-white">
                      {clientContactsArray.map((contact) => (
                        <SelectItem key={contact.key} value={contact.value}>
                          {contact.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-1/2 flex flex-col space-y-2">
                  <Label htmlFor="adress">Adresse</Label>
                  <Select name="adress" onValueChange={value => { if (value === "siege") setAdressFromSiege(); else if (value === "livraison") setAdressFromLivraison(); else if (value === "facturation") setAdressFromFacturation(); }}>
                    <SelectTrigger
                      className={`w-full ${missingFields.includes("adress") ? "border-red-500" : "border-gray-300"}`}
                      id="adress"
                    >
                      <SelectValue placeholder="Sélectionnez une adresse" />
                    </SelectTrigger>
                    <SelectContent className="z-[1100] bg-white">
                      {clientAdressesArray.map((address) => (
                        <SelectItem key={address.key} value={address.value}>
                          {address.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

        </section><section hidden={step !== 1}>
          <div className="space-y-2 mb-4">
            <Label htmlFor="typeContrat">Type de contrat</Label>
            <Select value={formData.typeContrat || undefined} onValueChange={selectTypeContrat} name="typeContrat">
              <SelectTrigger
                className={`w-full ${missingFields.includes("typeContrat") ? "border-red-500" : "border-gray-300"}`}
                id="typeContrat"
              >
                <SelectValue placeholder="Sélectionnez un type de contrat" />
              </SelectTrigger>
              <SelectContent className="z-[1100] bg-white">
                <SelectItem value="Intérim">Intérim</SelectItem>
                <SelectItem value="Vacation">Vacation</SelectItem>
                <SelectItem value="CDD">CDD</SelectItem>
                <SelectItem value="CDI">CDI</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.typeContrat === "Intérim" && <>
          <div className="space-y-2 mb-4">
            <Label htmlFor="motif">Motif de recours</Label>
            <Select value={formData.motif || undefined} onValueChange={selectMotif} name="motif">
              <SelectTrigger
                className={`w-full ${missingFields.includes("motif") ? "border-red-500" : "border-gray-300"}`}
                id="motif"
              >
                <SelectValue placeholder="Sélectionnez un motif de recours" />
              </SelectTrigger>
              <SelectContent className="z-[1100] bg-white">
                {usedMotif.map((motif) => {
                  return (
                    <SelectItem key={motif.key} value={motif.value}>
                      {motif.value}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 mb-4">
            <Label htmlFor="justificatif">Developper le motif</Label>
            <Input
              id="justificatif"
              type="text"
              placeholder="Justificatif"
              value={formData.justificatif}
              onChange={(e) =>
                setFormData({ ...formData, justificatif: e.target.value })
              }
              className={`w-full border  rounded-md pr-10 ${missingFields.includes("justificatif") ? "border-red-500" : "border-gray-300"}`}
            />
          </div>

          </>}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-40 flex-1 flex-col space-y-2 mb-4">
              <Label htmlFor="startDate">{multipleDates ? "Première date" : "Date de début"}</Label>
              <Input
                id="startDate"
                type="date"
                placeholder="Date de début"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md"
              />
            </div>

            {multipleDates && ["startHour", "endHour"].map(field => <label key={field} className="mb-4 min-w-32 flex-1 text-sm font-medium text-slate-700">{field === "startHour" ? "Heure de début" : "Heure de fin"}<Input type="time" value={formData[field] || ""} onChange={e => setFormData({...formData, [field]: e.target.value})} className="mt-2 w-full border border-gray-300 rounded-md" /></label>)}

            {!multipleDates && formData.typeContrat !== "CDI" && <div className="flex flex-col space-y-2 mb-4 w-1/2">
              <Label htmlFor="endDate">Date de fin</Label>
              <Input
                id="endDate"
                type="date"
                placeholder="Date de fin"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
                className="border  border-gray-300 rounded-md"
              />
            </div>}
          </div>

          {multipleDates && <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4">
            <p className="text-sm font-semibold text-blue-900">Dates supplémentaires</p>
            <p className="mt-1 text-xs text-slate-600">Chaque journée dispose de ses propres horaires. Une ligne correspond à une date et ses horaires.</p>
            <div className="mt-3 space-y-3">{extraPeriods.map((period,index) => <div key={index} className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-3"><label className="min-w-0 flex-1 text-xs font-medium">Date {index + 2}<input type="date" value={period.startDate} onChange={e => setExtraPeriods(current => current.map((p,i) => i === index ? {...p,startDate:e.target.value,endDate:e.target.value} : p))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" /></label>{["startHour","endHour"].map(field => <label key={field} className="min-w-0 flex-1 text-xs font-medium">{field === "startHour" ? "Heure de début" : "Heure de fin"}<input type="time" value={period[field] || ""} onChange={e => setExtraPeriods(current => current.map((p,i) => i === index ? {...p,[field]:e.target.value} : p))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" /></label>)}<button type="button" aria-label={`Supprimer la date ${index + 2}`} onClick={() => setExtraPeriods(current => current.filter((_,i) => i !== index))} className="rounded-lg px-3 py-2 text-sm text-red-700">Supprimer</button></div>)}</div>
            <button type="button" onClick={() => setExtraPeriods(current => [...current,{startDate:"",endDate:"",startHour:formData.startHour || "",endHour:formData.endHour || ""}])} className="mt-3 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700">+ Ajouter une date</button>
          </div>}

          <div className="space-y-6 my-8">
            {!multipleDates && <><Label>Horaire de la mission</Label>

            <div className="flex items-center space-x-4">
              <Input
                type="time"
                value={formData.startHour || ""}
                onChange={(e) =>
                  setFormData({ ...formData, startHour: e.target.value })
                }
                className="w-1/2 border border-gray-300 rounded-md"
                placeholder="Heure de début"
              />
              <Input
                type="time"
                value={formData.endHour || ""}
                onChange={(e) =>
                  setFormData({ ...formData, endHour: e.target.value })
                }
                className="w-1/2 border border-gray-300 rounded-md"
                placeholder="Heure de fin"
              />
            </div>

            </>}

            <div>
              <Label htmlFor="nbHeureHebdo">Nombre heures hebdomadaire</Label>

              <Input
                type="number"
                placeholder="Nombre heures hebdomadaire"
                value={formData.nbHeureHebdo}
                onChange={(e) => selectNombreHeureHebdo(e.target.value)}
                className="border border-gray-300 rounded-md mt-4"
              />
            </div>
          </div>

        </section><section hidden={step !== 2}>
          <div>
            <div className="mt-6">
              <Label>Lieu de la mission</Label>
              <div className="flex space-x-2 items-center justify-between mt-2 mb-4 text-sm">
                <p
                  onClick={() => setAdressFromSiege()}
                  className="text-blue-500 cursor-pointer hover:underline"
                >
                  Recopier l'adresse du siège
                </p>
                <p
                  onClick={() => setAdressFromLivraison()}
                  className="text-blue-500 cursor-pointer hover:underline"
                >
                  Recopier l'adresse de livraison
                </p>
                <p
                  onClick={() => setAdressFromFacturation()}
                  className="text-blue-500 cursor-pointer hover:underline"
                >
                  Recopier l'adresse de facturation
                </p>
              </div>
              <div className="flex flex-col items-center space-y-3">
                <Input
                  value={formData.clientName}
                  onChange={(e) =>
                    setFormData({ ...formData, clientName: e.target.value })
                  }
                  className="rounded-md border border-gray-300"
                  placeholder="Nom du client final"
                />
                <Input
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="rounded-md border border-gray-300"
                  placeholder="Adresse"
                />
                <Input
                  value={formData.postCode}
                  onChange={(e) =>
                    setFormData({ ...formData, postCode: e.target.value })
                  }
                  className="rounded-md border border-gray-300"
                  placeholder="Code postal"
                />
                <Input
                  value={formData.town}
                  onChange={(e) =>
                    setFormData({ ...formData, town: e.target.value })
                  }
                  className="rounded-md border border-gray-300"
                  placeholder="Ville"
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <LazyLoadingSelectQualification
              qualifications={qualifications}
              formData={formData}
              setFormData={setFormData}
            />
            <Label>Nombre de personnes recherchées</Label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => selectQuantity(formData.quantity - 1)}
                className="text-xl"
              >
                -
              </button>
              <span className="text-xl">{formData.quantity}</span>
              <button
                type="button"
                onClick={() => selectQuantity(formData.quantity + 1)}
                className="text-xl"
              >
                +
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div>
              <Label>Renseigner le taux horaire</Label>
              <div className="flex flex-col items-center space-y-3 mt-4">
                <Input
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hourlyRate: e.target.value,
                    }))
                  }
                  value={formData.hourlyRate}
                  className="rounded-md border border-gray-300"
                  placeholder="Taux horaire"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col mt-6  rounded-xl shadow-sm mb-6 bg-white">
            <Label className="mb-2">Renseigner primes/indemnités</Label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="flex flex-col space-y-4">
                {/* Catégorie (Primes/Indemnités) */}
                <div>
                  <Label>Catégorie</Label>
                  <Select
                    onValueChange={(value) => setPrime(value)}
                    value={prime}
                  >
                    <SelectTrigger className="w-full" id="prime">
                      <SelectValue placeholder="Sélectionnez une catégorie" />
                    </SelectTrigger>
                    <SelectContent className="z-[1100] bg-white">
                      {[
                        { value: "Primes", label: "Primes", key: "Primes" },
                        {
                          value: "Indemnités",
                          label: "Indemnités",
                          key: "Indemnités",
                        },
                      ].map((item) => (
                        <SelectItem key={item.key} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Type de prime */}
                <div>
                  <Label>Type de prime</Label>
                  <Input
                    onChange={(e) => setPrimeType(e.target.value)}
                    value={primeType}
                    className="rounded-md border border-gray-300"
                    placeholder="Type de prime"
                  />
                </div>

                {/* Taux ou montant */}
                <div>
                  <Label>Taux ou montant</Label>
                  <Input
                    onChange={(e) => setTaux(e.target.value)}
                    value={taux}
                    className="rounded-md border border-gray-300"
                    placeholder="Taux ou montant"
                  />
                </div>
              </div>

              {/* Bouton d'ajout */}
              <div className="flex items-end justify-start">
                <button
                  type="button"
                  onClick={addPrime}
                  className="rounded-lg bg-blue-500 px-6 py-1 text-white hover:bg-mountain-600 transition flex items-center space-x-2"
                >
                  <ShoppingBagIcon className="w-5 h-5 " />
                  <span>Ajouter</span>
                </button>
              </div>
            </div>

            {formData.primes.length > 0 && (
              <div className="overflow-x-auto">
                <div className="grid grid-cols-4 gap-2 bg-gray-100 py-2 px-4 rounded-md text-sm font-semibold uppercase text-gray-600">
                  <p className="text-center">Prime</p>
                  <p className="text-center">Type de prime</p>
                  <p className="text-center">Taux / Montant</p>
                  <p className="text-center">Actions</p>
                </div>

                {formData.primes.map((prime, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-4 gap-2 border-b py-2 px-4 text-sm items-center"
                  >
                    <p className="text-center">{prime.prime}</p>
                    <p className="text-center">{prime.primeType}</p>
                    <p className="text-center">{prime.taux}</p>
                    <p className="text-center">
                      <button type="button"
                        onClick={() => deletePrime(idx)}
                        className="text-red-500 hover:text-red-700 transition"
                      >
                        <DeleteIcon className="w-5 h-5 text-red" />
                      </button>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

        </section><section hidden={step !== 3}>
          <div className="flex flex-col mt-6  rounded-xl shadow-sm mb-6 bg-white">
            <Label>Consignes particulières</Label>

            <div className="my-4">
              <Select value={formData.consigne} onValueChange={selectConsigne}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionnez une consigne" />
                </SelectTrigger>
                <SelectContent className="z-[1100] bg-white max-h-60 overflow-auto">
                  {[
                    { value: "0 : Aucune", key: "0" },
                    {
                      value:
                        "1 : Port des EPI obligatoire. Le respect des règles de sécurité et d'hygiène est obligatoire.",
                      key: "1",
                    },
                    {
                      value:
                        "2 : La formation sécurité est dispensée par le responsable en poste le jour de l'intégration.",
                      key: "2",
                    },
                    {
                      value:
                        "3 : Port des EPI obligatoire. Le respect des règles de sécurité et d'hygiène est obligatoire. La formation sécurité est dispensée par le responsable en poste le jour de l'intégration.",
                      key: "3",
                    },
                    {
                      value:
                        "4 : Port des EPI obligatoire. Le respect des règles de sécurité et d'hygiène est obligatoire. L'assurance du véhicule est à la charge de l'entreprise utilisatrice.",
                      key: "4",
                    },
                  ].map((option) => (
                    <SelectItem key={option.key} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col mt-6 rounded-xl shadow-sm mb-6 bg-white">
            <Label className="mb-2">Être véhiculé(e) ?</Label>

            <div className="flex items-center space-x-10 mb-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={formData.isVehicule}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isVehicule: e.target.checked,
                    }))
                  }
                />
                <span>OUI</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <Checkbox
                  checked={!formData.isVehicule}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isVehicule: !e.target.checked,
                    }))
                  }
                />
                <span>NON</span>
              </label>
            </div>

            <Label className="text-lg mb-4">Prérequis</Label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MultiSelect
                options={diplomasArray.map((diploma) => ({
                  label: diploma,
                  value: diploma,
                }))}
                selected={diplomes}
                setSelected={setDiplomes}
                placeholder="Sélectionnez les diplômes"
              />

              <MultiSelect
                options={habilitationsList.map((habili) => ({
                  label: habili,
                  value: habili,
                }))}
                selected={habilitations}
                setSelected={setHabilitations}
                placeholder="Sélectionnez les habilitations"
              />

              <MultiSelect
                options={cacesList.map((caces) => ({
                  label: caces,
                  value: caces,
                }))}
                selected={caces}
                setSelected={setCaces}
                placeholder="Sélectionnez les CACES"
              />

              <MultiSelect
                options={licencesArray.map((licence) => ({
                  label: licence,
                  value: licence,
                }))}
                selected={permis}
                setSelected={setPermis}
                placeholder="Sélectionnez les permis"
              />
            </div>
          </div>

          <div className="flex flex-col mt-6 bg-white">
            <div>
              <Label className="mb-2">Quelles sont les tâches/missions ?</Label>

              <div className="flex items-center my-6 justify-center">
                <Textarea
                  className="h-24 w-full"
                  onChange={(e) => setTask(e.target.value)}
                  value={formData.task}
                  placeholder="Précisez les missions à effectuer..."
                />
              </div>

              <p className="text-red-500 text-center font-semibold text-sm">
                ⚠️ Ne pas mentionner le nom du client
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
            <p className="font-semibold">Récapitulatif de la commande</p>
            <p className="mt-2">{formData.qualification || "Qualification à renseigner"} · {formData.quantity} personne(s) · {formData.typeContrat || "Type à renseigner"}</p>
            <p className="mt-1">{formData.startDate || "Début à renseigner"}{!multipleDates && formData.endDate && ` → ${formData.endDate}`} · {formData.startHour || "—"}–{formData.endHour || "—"}</p>
            {multipleDates && extraPeriods.map((period,index) => <p key={index} className="mt-1">Date {index + 2} : {period.startDate || "—"} · {period.startHour || "—"}–{period.endHour || "—"}</p>)}
            <p className="mt-1">{formData.nbHeureHebdo || "—"} h / semaine</p>
            <p className="mt-1">{[formData.address, formData.postCode, formData.town].filter(Boolean).join(" ")}</p>
            <p className="mt-1">Taux horaire : {formData.hourlyRate || "—"} €</p>
          </div>
        </section></div>
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <Button type="button" disabled={isLoading} variant="outline" onClick={() => step ? setStep(step - 1) : close()}>{step ? "Précédent" : "Annuler"}</Button>
          <span className="text-xs text-slate-400">Étape {step + 1} sur 4</span>
          <Button type="submit" disabled={isLoading} className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800">{isLoading ? <><Loader2 size={16} className="animate-spin" />{isEdit ? "Enregistrement…" : "Création…"}</> : step < 3 ? "Continuer" : isEdit ? "Enregistrer les modifications" : isDuplicate ? "Créer la copie" : "Créer la commande"}</Button>
        </div>
      </form>
    </Modal>
  );
};
export default CreateCommandeModal;
